/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, useLocation, Outlet } from 'react-router'
import { useAccessControl } from '../hooks/useAccessControl'
import { useServiceContext } from '../root'
import { usePageContext } from '../contexts/page-state'

/**
 * React equivalent of staging.stateProvider.js
 *
 * This component handles:
 * 1. Access control check for 'stagingUsers' (equivalent to accessTable.stagingAccess)
 * 2. Staging context initialization from URL parameters and localStorage
 * 3. Context ID validation before allowing navigation to staging pages
 * 4. Integration with global PageState for consistent staging state management
 */
export default function StagingLayout() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAllowed } = useAccessControl()
  const { service } = useServiceContext()
  const pageContext = usePageContext()

  const [isInitialized, setIsInitialized] = useState(false)
  const [hasAccess, setHasAccess] = useState<boolean | null>(null)
  const [contextId, setContextId] = useState<string | number | null>(null)

  // Check access permission (equivalent to AccessService.isAllowedStateAsync)
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const hasAccessToStaging = isAllowed('stagingAccess')
        setHasAccess(hasAccessToStaging)

        if (!hasAccessToStaging) {
          console.warn('Access denied to staging area')
          navigate('/', { replace: true })
          return
        }
      } catch (error) {
        console.error('Error checking staging access:', error)
        setHasAccess(false)
        navigate('/', { replace: true })
      }
    }

    checkAccess()
  }, [isAllowed, navigate])

  // Initialize staging context (equivalent to contextId resolver)
  useEffect(() => {
    if (hasAccess === false) return

    const initializeStagingContext = async () => {
      try {
        // Get URL parameters
        const siteId = searchParams.get('siteId')
        const studyId = searchParams.get('studyId')
        const environmentId = searchParams.get('environmentId')
        const propertySearchId = searchParams.get('propertySearchId')

        // Set staging context IDs (with fallback to localStorage)
        if (environmentId || service.staging.getLocalStorageData('environmentId')) {
          service.staging.setEnvironmentId(environmentId || service.staging.getLocalStorageData('environmentId'))
        }

        if (studyId || service.staging.getLocalStorageData('studyId')) {
          service.staging.setStudyId(studyId || service.staging.getLocalStorageData('studyId'))
        }

        if (siteId || service.staging.getLocalStorageData('siteId')) {
          service.staging.setSiteId(siteId || service.staging.getLocalStorageData('siteId'))
        }

        if (propertySearchId || service.staging.getLocalStorageData('propertySearchId')) {
          service.staging.setPropertySearchId(
            propertySearchId || service.staging.getLocalStorageData('propertySearchId')
          )
        }

        // Get context ID after setting all parameters
        const currentContextId = service.staging.getContextId()

        if (currentContextId) {
          setContextId(currentContextId)
          setIsInitialized(true)

          // Force page context to update staging selection state
          // This ensures PageState.tsx reflects the current staging context
          // The PageState monitoring will pick this up automatically
        } else {
          // No valid context ID - redirect to staging overview or show error
          console.warn('No valid staging context available')
          // Don't redirect if we're already on overview page
          if (!location.pathname.includes('/staging/overview')) {
            navigate('/staging/overview', { replace: true })
          } else {
            // Allow overview page even without context
            setIsInitialized(true)
          }
        }
      } catch (error) {
        console.error('Error initializing staging context:', error)
        setIsInitialized(false)
        // Could redirect to error page or staging overview
        navigate('/staging/overview', { replace: true })
      }
    }

    if (hasAccess === true) {
      initializeStagingContext()
    }
  }, [hasAccess, searchParams, service.staging, navigate])

  // Show loading state while checking access and initializing
  if (hasAccess === null || (hasAccess === true && !isInitialized)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading staging area...</span>
        </div>
      </div>
    )
  }

  // Show access denied state
  if (hasAccess === false) {
    return (
      <div className="alert alert-warning m-4">
        <h4>Access Denied</h4>
        <p>You do not have permission to access the staging area.</p>
      </div>
    )
  }

  // Render child routes with staging context available
  return (
    <div className="d-flex flex-column flex-grow justify-content-start align-items-stretch">
      {/* This div structure matches the AngularJS template */}
      <Outlet context={{ contextId, isInitialized }} />
    </div>
  )
}

/**
 * Hook to access staging context from child components
 */
export function useStagingContext() {
  const { service } = useServiceContext()

  return {
    contextId: service.staging.getContextId(),
    siteId: service.staging.getSiteId(),
    studyId: service.staging.getStudyId(),
    environmentId: service.staging.getEnvironmentId(),
    propertySearchId: service.staging.getPropertySearchId(),
    level: service.staging.getLevel(),
    setSiteId: service.staging.setSiteId.bind(service.staging),
    setStudyId: service.staging.setStudyId.bind(service.staging),
    setEnvironmentId: service.staging.setEnvironmentId.bind(service.staging),
    setPropertySearchId: service.staging.setPropertySearchId.bind(service.staging)
  }
}
